import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Spin, Typography, Empty, Select, Button, Modal } from "antd";
import { LinkOutlined } from "@ant-design/icons";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { getPublicBalancedScorecard, comparePublicVersions } from "./balancedScorecard.service";
import type { HistoryListResponse, ApiScorecardFull, CompareResponse } from "./balancedScorecard.service";
import "./BalancedScorecardPage.css";

const { Title, Text } = Typography;

// Safe number formatting
const formatScore = (value: any): string => {
  if (value === null || value === undefined) return "—";
  const num = typeof value === "string" ? parseFloat(value) : value;
  return isNaN(num) ? "—" : num.toFixed(2);
};

const PublicBalancedScorecardHistoryView: React.FC = () => {
  const { token } = useParams<{ token: string }>();

  const [loading, setLoading] = useState(true);
  const [scorecard, setScorecard] = useState<ApiScorecardFull | null>(null);
  const [history, setHistory] = useState<HistoryListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [compareV1, setCompareV1] = useState<string | undefined>(undefined);
  const [compareV2, setCompareV2] = useState<string | undefined>(undefined);
  const [compareResult, setCompareResult] = useState<CompareResponse | null>(null);
  const [compareLoading, setCompareLoading] = useState(false);
  const [compareModalOpen, setCompareModalOpen] = useState(false);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        setLoading(true);
        const data = await getPublicBalancedScorecard(token);
        console.log("Public scorecard data:", data);
        setScorecard(data.scorecard);
        setHistory(data.history);
      } catch (err: any) {
        const status = err?.response?.status;
        if (status === 403) {
          setError("This link has been disabled by the owner.");
        } else if (status === 404) {
          setError("Scorecard not found.");
        } else {
          setError("Failed to load scorecard.");
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleCompare = async () => {
    if (!token || !compareV1 || !compareV2) return;
    setCompareLoading(true);
    try {
      const result = await comparePublicVersions(token, compareV1, compareV2);
      setCompareResult(result);
      setCompareModalOpen(true);
    } catch (err) {
      console.error("Failed to compare versions", err);
    } finally {
      setCompareLoading(false);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (error || !scorecard || !history) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          gap: 12,
          color: "#888",
        }}
      >
        <LinkOutlined style={{ fontSize: 40, color: "#ccc" }} />
        <Title level={4} style={{ margin: 0, color: "#555" }}>
          {error || "Scorecard not found."}
        </Title>
        <Text style={{ color: "#aaa", fontSize: 13 }}>
          The link may have expired or been disabled.
        </Text>
      </div>
    );
  }

  const snapshotOptions = history.snapshots.map((s) => ({
    label: `v${s.version_number} - ${new Date(s.published_at).toLocaleDateString()}`,
    value: s.id,
  }));

  return (
    <div className="public-bsc-history-wrapper" style={{ padding: "24px", maxWidth: 1400, margin: "0 auto" }}>
      <div className="public-bsc-history-container">
        {/* Header Section */}
        <div style={{ marginBottom: 32 }}>
          <h1 style={{ margin: "0 0 8px", color: "#333", fontSize: 24, fontWeight: 700 }}>
            History
          </h1>
          <p style={{ margin: 0, color: "#999", fontSize: 13, lineHeight: 1.6 }}>
            Every time you publish, the score is screenshotted & frozen into an entry — an audit trail of how performance evolved across reporting periods.
          </p>
        </div>

        {!history.snapshots || history.snapshots.length === 0 ? (
          <Empty
            description="No published versions yet"
            style={{ marginTop: 48 }}
          />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
            {/* Score over time - Chart Section */}
            <div
              style={{
                background: "#fff",
                border: "1px solid #e8e8e8",
                borderRadius: 6,
                padding: 24,
              }}
            >
              <p style={{ margin: "0 0 20px", fontSize: 13, fontWeight: 600, color: "#333" }}>
                Score over time
              </p>

              {/* Line chart with all domains */}
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={history.trend || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e8" />
                  <XAxis
                    dataKey="published_at"
                    tickFormatter={(date) =>
                      new Date(date).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                      })
                    }
                    stroke="#999"
                    style={{ fontSize: 12 }}
                  />
                  <YAxis stroke="#999" style={{ fontSize: 12 }} domain={[0, 100]} />
                  <Tooltip
                    contentStyle={{
                      background: "#fff",
                      border: "1px solid #e8e8e8",
                      borderRadius: 4,
                    }}
                    formatter={(value) => formatScore(value)}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: 12 }}
                    verticalAlign="top"
                    align="right"
                    height={36}
                  />
                  <Line
                    type="monotone"
                    dataKey="overall_score"
                    name="Overall"
                    stroke="#5540af"
                    dot={{ fill: "#5540af", r: 5 }}
                    strokeWidth={2}
                  />
                  {history.trend?.[0]?.domains?.map((domain) => (
                    <Line
                      key={domain.name}
                      type="monotone"
                      dataKey={(data: any) => data.domains?.find((d: any) => d.name === domain.name)?.domain_score}
                      name={domain.name}
                      stroke={domain.color}
                      dot={false}
                      strokeDasharray="5 5"
                      strokeWidth={2}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Published versions table */}
            <div style={{ background: "#fff", border: "1px solid #e8e8e8", borderRadius: 6, padding: 24 }}>
              <h3 style={{ margin: "0 0 16px", fontSize: 13, fontWeight: 600, color: "#333" }}>
                Published
              </h3>
              <div style={{ overflowX: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: 13,
                  }}
                >
                  <thead>
                    <tr style={{ borderBottom: "1px solid #e8e8e8" }}>
                      <th style={{ textAlign: "left", padding: "12px 0", fontWeight: 600, color: "#666", fontSize: 12 }}>
                        Date
                      </th>
                      <th style={{ textAlign: "left", padding: "12px 0", fontWeight: 600, color: "#666", fontSize: 12 }}>
                        Score
                      </th>
                      <th style={{ textAlign: "left", padding: "12px 0", fontWeight: 600, color: "#666", fontSize: 12 }}>
                        Status
                      </th>
                      {history.snapshots[0]?.domains?.map((domain) => (
                        <th
                          key={domain.name}
                          style={{
                            textAlign: "left",
                            padding: "12px 12px 12px 0",
                            fontWeight: 600,
                            color: domain.color || "#666",
                            fontSize: 12,
                          }}
                        >
                          {domain.name}
                        </th>
                      ))}
                      <th style={{ textAlign: "right", padding: "12px 0", fontWeight: 600, color: "#666", fontSize: 12 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.snapshots.map((snapshot) => (
                      <tr key={snapshot.id} style={{ borderBottom: "1px solid #f0f0f0" }}>
                        <td style={{ padding: "16px 0", color: "#333" }}>
                          <div>
                            <div style={{ fontWeight: 600, marginBottom: 2 }}>
                              {new Date(snapshot.published_at).toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              })}
                            </div>
                            <div style={{ fontSize: 12, color: "#999" }}>
                              by {snapshot.published_by?.name || "Unknown"}
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: "16px 0", color: "#f59e0b", fontWeight: 600 }}>
                          {formatScore(snapshot.overall_score)}
                        </td>
                        <td style={{ padding: "16px 0" }}>
                          <div
                            style={{
                              display: "inline-block",
                              padding: "4px 12px",
                              borderRadius: 4,
                              background: "#dcfce7",
                              color: "#16a34a",
                              fontSize: 11,
                              fontWeight: 600,
                            }}
                          >
                            {formatScore(snapshot.overall_score)}
                          </div>
                        </td>
                        {snapshot.domains?.map((domain) => (
                          <td
                            key={`${snapshot.id}-${domain.name}`}
                            style={{
                              padding: "16px 12px 16px 0",
                              color: domain.color || "#4A1C96",
                              fontWeight: 600,
                              fontSize: 13,
                            }}
                          >
                            {formatScore(domain.domain_score)}
                          </td>
                        ))}
                        <td style={{ padding: "16px 0", textAlign: "right" }}>
                          <button
                            style={{
                              background: "none",
                              border: "none",
                              color: "#7c3aed",
                              fontWeight: 600,
                              cursor: "pointer",
                              fontSize: 13,
                            }}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>


            {/* Compare versions */}
            {history.snapshots.length > 1 && (
              <div
                style={{
                  border: "1px solid #e8e8e8",
                  borderRadius: 6,
                  padding: 24,
                  background: "#fff",
                }}
              >
                <h3 style={{ margin: "0 0 6px", fontSize: 13, fontWeight: 600, color: "#333" }}>
                  Compare versions
                </h3>
                <p style={{ margin: "0 0 20px", fontSize: 13, color: "#999" }}>
                  Side by side delta between two published snapshots.
                </p>
                <div style={{ display: "flex", gap: 12, alignItems: "flex-end" }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: "0 0 8px", fontSize: 12, color: "#666" }}>
                      Version A
                    </p>
                    <Select
                      placeholder="Select version A"
                      options={snapshotOptions}
                      value={compareV1}
                      onChange={setCompareV1}
                      style={{ width: "100%" }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: "0 0 8px", fontSize: 12, color: "#666" }}>
                      Version B
                    </p>
                    <Select
                      placeholder="Select version B"
                      options={snapshotOptions}
                      value={compareV2}
                      onChange={setCompareV2}
                      style={{ width: "100%" }}
                    />
                  </div>
                  <Button
                    onClick={handleCompare}
                    disabled={!compareV1 || !compareV2}
                    loading={compareLoading}
                    style={{
                      background: "#4A1C96",
                      color: "#fff",
                      borderColor: "#4A1C96",
                      fontWeight: 600,
                    }}
                  >
                    Compare
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Compare Modal */}
      {compareResult && (
        <Modal
          open={compareModalOpen}
          onCancel={() => setCompareModalOpen(false)}
          width={800}
          title="Version Comparison"
          footer={null}
          centered
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Overall delta */}
            <div style={{ textAlign: "center", padding: 16, background: "#f9f9f9", borderRadius: 8 }}>
              <p style={{ margin: 0, fontSize: 12, color: "#666" }}>Overall Score Delta</p>
              <p
                style={{
                  margin: "8px 0 0",
                  fontSize: 24,
                  fontWeight: 700,
                  color: compareResult.overall_delta && compareResult.overall_delta > 0 ? "#16a34a" : "#dc2626",
                }}
              >
                {compareResult.overall_delta !== null
                  ? `${compareResult.overall_delta > 0 ? "+" : ""}${formatScore(compareResult.overall_delta)}`
                  : "—"}
              </p>
            </div>

            {/* Domain comparison */}
            {compareResult.domains?.map((domain, idx) => (
              <div key={idx} style={{ border: "1px solid #e8e8e8", borderRadius: 6, padding: 12 }}>
                <p style={{ margin: "0 0 12px", fontSize: 13, fontWeight: 600, color: "#333" }}>
                  {domain.name}
                </p>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                  <div>
                    <p style={{ margin: 0, fontSize: 11, color: "#666" }}>Version A</p>
                    <p style={{ margin: "4px 0 0", fontSize: 16, fontWeight: 600, color: "#4A1C96" }}>
                      {formatScore(domain.score_v1)}
                    </p>
                  </div>
                  <div>
                    <p style={{ margin: 0, fontSize: 11, color: "#666" }}>Delta</p>
                    <p
                      style={{
                        margin: "4px 0 0",
                        fontSize: 16,
                        fontWeight: 600,
                        color: domain.delta && domain.delta > 0 ? "#16a34a" : "#dc2626",
                      }}
                    >
                      {domain.delta !== null ? `${domain.delta > 0 ? "+" : ""}${formatScore(domain.delta)}` : "—"}
                    </p>
                  </div>
                  <div>
                    <p style={{ margin: 0, fontSize: 11, color: "#666" }}>Version B</p>
                    <p style={{ margin: "4px 0 0", fontSize: 16, fontWeight: 600, color: "#4A1C96" }}>
                      {formatScore(domain.score_v2)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Modal>
      )}

      <div style={{ textAlign: "center", marginTop: 48, color: "#bbb", fontSize: 12 }}>
        Powered by <strong style={{ color: "#A072EB" }}>KAPinsights</strong>
      </div>
    </div>
  );
};

export default PublicBalancedScorecardHistoryView;
